import * as THREE from 'three';
import type { Game, GameContext, GameMeta, InputState } from '../types';
import {
  createThreeKit,
  disposeObject,
  makeGlowDisc,
  makeStars,
  resizeThree,
  type ThreeKit,
} from '../shared/threeKit';
import { OrbitRig } from '../shared/orbitRig';

type Bullet = { mesh: THREE.Mesh; vx: number; vy: number };
type Enemy = { mesh: THREE.Group; angle: number; r: number; vr: number; hp: number; kind: string };
type Power = { mesh: THREE.Group; angle: number; r: number; kind: 'multi' | 'rapid' | 'shield' };

/**
 * SIDE RAID — orbit the core, shoot inward.
 * ←→ orbit · ↑↓ radius · FIRE toward center · powerups
 */
export class SideRaidGame implements Game {
  readonly meta: GameMeta = {
    id: 'rtype',
    title: 'SIDE RAID',
    blurb: 'Orbit the core. Shoot the center. Grab powerups. Drag cam.',
    tag: 'ARCADE',
    status: 'playable',
  };

  private ctx!: GameContext;
  private kit!: ThreeKit;
  private stars!: THREE.Points;
  private ship!: THREE.Group;
  private core!: THREE.Group;
  private bullets: Bullet[] = [];
  private enemies: Enemy[] = [];
  private powers: Power[] = [];
  private particles: { mesh: THREE.Mesh; life: number; vx: number; vy: number }[] = [];
  private angle = 0;
  private radius = 8.5;
  private fireCd = 0;
  private spawnCd = 0.35;
  private powerCd = 4;
  private invuln = 0;
  private shieldT = 0;
  private multiT = 0;
  private rapidT = 0;
  private score = 0;
  private lives = 3;
  private wave = 1;
  private message = 'ORBIT · FIRE CENTER · DRAG CAM';
  private messageT = 2.4;
  private disposed = false;
  private orbit = new OrbitRig({ azimuth: 0.2, elevation: 0.55, distance: 22 });
  private camTarget = new THREE.Vector3(0, 0, 0);
  private readonly rMin = 5.5;
  private readonly rMax = 11;

  async mount(ctx: GameContext): Promise<void> {
    this.ctx = ctx;
    this.kit = createThreeKit(ctx, {
      label: 'Side Raid',
      z: 24,
      fov: 45,
      clear: 0x0a1422,
      fog: 0x0a1422,
      fogNear: 25,
      fogFar: 55,
    });
    this.kit.scene.add(new THREE.AmbientLight(0xb0d8ff, 0.7));
    this.kit.scene.add(new THREE.HemisphereLight(0xa0e8ff, 0x223344, 0.55));
    this.stars = makeStars(this.kit.lowPower ? 90 : 200, 0xaaddff);
    this.kit.scene.add(this.stars);

    // arena disc
    const disc = new THREE.Mesh(
      new THREE.CircleGeometry(12.5, 64),
      new THREE.MeshStandardMaterial({
        color: 0x12283a,
        emissive: 0x0a1a28,
        emissiveIntensity: 0.55,
        roughness: 0.85,
      }),
    );
    disc.rotation.x = -Math.PI / 2;
    disc.position.y = -0.05;
    this.kit.root.add(disc);

    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(12.2, 0.06, 8, 64),
      new THREE.MeshBasicMaterial({ color: 0x44ffcc, transparent: true, opacity: 0.45 }),
    );
    ring.rotation.x = Math.PI / 2;
    this.kit.root.add(ring);

    // glowing core
    this.core = new THREE.Group();
    const coreBall = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.1, 1),
      new THREE.MeshStandardMaterial({
        color: 0x66ffee,
        emissive: 0x22aacc,
        emissiveIntensity: 0.9,
        roughness: 0.25,
        metalness: 0.4,
      }),
    );
    const coreGlow = makeGlowDisc(0x66eeff, 2.4, 0.4);
    coreGlow.rotation.x = -Math.PI / 2;
    this.core.add(coreBall, coreGlow);
    this.kit.root.add(this.core);

    this.ship = new THREE.Group();
    const body = new THREE.Mesh(
      new THREE.ConeGeometry(0.45, 1.3, 4),
      new THREE.MeshStandardMaterial({
        color: 0x88ffbb,
        emissive: 0x228855,
        emissiveIntensity: 0.75,
        roughness: 0.3,
      }),
    );
    // point inward when placed
    const engine = makeGlowDisc(0xaaffdd, 0.8, 0.55);
    engine.position.y = -0.55;
    this.ship.add(body, engine);
    this.kit.root.add(this.ship);

    this.orbit.attach(this.kit.renderer.domElement);
    const rect = ctx.mount.getBoundingClientRect();
    this.resize(rect.width || 320, rect.height || 480);
  }

  private placeShip(): void {
    const x = Math.cos(this.angle) * this.radius;
    const z = Math.sin(this.angle) * this.radius;
    this.ship.position.set(x, 0.35, z);
    // face center
    this.ship.lookAt(0, 0.35, 0);
    this.ship.rotateX(Math.PI / 2);
  }

  private spawnEnemy(energy: number): void {
    const kind = Math.random() < 0.25 ? 'tank' : Math.random() < 0.2 ? 'gem' : 'drone';
    const g = new THREE.Group();
    let color = 0xff6a8a;
    let geo: THREE.BufferGeometry = new THREE.OctahedronGeometry(0.45, 0);
    let hp = 1;
    let r = 0.8 + Math.random() * 1.2;
    let vr = 2.2 + energy * 2.5 + this.wave * 0.15;
    if (kind === 'tank') {
      color = 0xffaa44;
      geo = new THREE.BoxGeometry(0.9, 0.9, 0.9);
      hp = 3;
      vr *= 0.55;
    } else if (kind === 'gem') {
      color = 0x66ccff;
      geo = new THREE.TetrahedronGeometry(0.4);
      vr *= 1.15;
    }
    const body = new THREE.Mesh(
      geo,
      new THREE.MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity: 0.5,
        roughness: 0.35,
      }),
    );
    g.add(body, makeGlowDisc(color, 0.75, 0.35));
    const ang = Math.random() * Math.PI * 2;
    g.position.set(Math.cos(ang) * r, 0.4, Math.sin(ang) * r);
    this.kit.root.add(g);
    this.enemies.push({ mesh: g, angle: ang, r, vr, hp, kind });
  }

  private spawnPower(): void {
    const kinds: Power['kind'][] = ['multi', 'rapid', 'shield'];
    const kind = kinds[Math.floor(Math.random() * kinds.length)];
    const colors = { multi: 0xff66cc, rapid: 0xffee66, shield: 0x66aaff };
    const g = new THREE.Group();
    const ball = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.35, 0),
      new THREE.MeshStandardMaterial({
        color: colors[kind],
        emissive: colors[kind],
        emissiveIntensity: 0.7,
      }),
    );
    g.add(ball, makeGlowDisc(colors[kind], 0.7, 0.45));
    const ang = Math.random() * Math.PI * 2;
    const r = 4 + Math.random() * 5;
    g.position.set(Math.cos(ang) * r, 0.5, Math.sin(ang) * r);
    this.kit.root.add(g);
    this.powers.push({ mesh: g, angle: ang, r, kind });
  }

  private burst(x: number, y: number, z: number, color: number): void {
    const n = this.kit.lowPower ? 5 : 10;
    for (let i = 0; i < n; i++) {
      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(0.1, 6, 6),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.95 }),
      );
      mesh.position.set(x, y, z);
      this.kit.root.add(mesh);
      const a = Math.random() * Math.PI * 2;
      this.particles.push({
        mesh,
        life: 0.4,
        vx: Math.cos(a) * 5,
        vy: Math.sin(a) * 5,
      });
    }
  }

  private hit(): void {
    if (this.shieldT > 0) {
      this.shieldT = 0;
      this.invuln = 0.8;
      this.message = 'SHIELD BROKE';
      this.messageT = 0.7;
      return;
    }
    this.lives -= 1;
    this.invuln = 1.4;
    if (navigator.vibrate) navigator.vibrate([16, 28, 16]);
    if (this.lives <= 0) {
      this.message = 'GAME OVER — FIRE TO RETRY';
      this.messageT = 99;
    } else {
      this.message = 'HIT';
      this.messageT = 0.7;
    }
  }

  private reset(): void {
    this.score = 0;
    this.lives = 3;
    this.wave = 1;
    this.angle = 0;
    this.radius = 8.5;
    this.shieldT = 0;
    this.multiT = 0;
    this.rapidT = 0;
    this.message = 'ORBIT THE CORE';
    this.messageT = 1.5;
    for (const e of this.enemies) {
      this.kit.root.remove(e.mesh);
      disposeObject(e.mesh);
    }
    for (const b of this.bullets) {
      this.kit.root.remove(b.mesh);
      disposeObject(b.mesh);
    }
    for (const p of this.powers) {
      this.kit.root.remove(p.mesh);
      disposeObject(p.mesh);
    }
    for (const p of this.particles) {
      this.kit.root.remove(p.mesh);
      disposeObject(p.mesh);
    }
    this.enemies = [];
    this.bullets = [];
    this.powers = [];
    this.particles = [];
  }

  update(dt: number, input: InputState): void {
    if (this.disposed) return;
    const bands = this.ctx.getBands();
    const { energy, kick, beat, bass } = bands;
    const t = performance.now() * 0.001;

    if (this.messageT > 0) this.messageT -= dt;
    this.fireCd = Math.max(0, this.fireCd - dt);
    this.spawnCd = Math.max(0, this.spawnCd - dt);
    this.powerCd = Math.max(0, this.powerCd - dt);
    this.invuln = Math.max(0, this.invuln - dt);
    this.shieldT = Math.max(0, this.shieldT - dt);
    this.multiT = Math.max(0, this.multiT - dt);
    this.rapidT = Math.max(0, this.rapidT - dt);

    if (this.lives <= 0) {
      if (input.firePressed) this.reset();
      this.orbit.apply(this.kit.camera, this.camTarget, dt);
      this.kit.renderer.render(this.kit.scene, this.kit.camera);
      return;
    }

    // orbit controls
    const orbitSpeed = 1.9 + energy * 0.8;
    if (input.left) this.angle += orbitSpeed * dt;
    if (input.right) this.angle -= orbitSpeed * dt;
    if (input.up) this.radius = Math.max(this.rMin, this.radius - 6 * dt);
    if (input.down) this.radius = Math.min(this.rMax, this.radius + 6 * dt);
    this.placeShip();
    this.ship.visible = this.invuln <= 0 || Math.floor(this.invuln * 14) % 2 === 0;

    // core pulse
    const coreScale = 1 + kick * 0.15 + bass * 0.08;
    this.core.scale.setScalar(coreScale);
    this.core.rotation.y += dt * (0.6 + energy);

    // fire toward center
    const fireRate = this.rapidT > 0 ? 0.07 : Math.max(0.1, 0.18 - energy * 0.05);
    if ((input.firePressed || input.fire) && this.fireCd <= 0) {
      const sx = this.ship.position.x;
      const sz = this.ship.position.z;
      const len = Math.hypot(sx, sz) || 1;
      const dirx = -sx / len;
      const dirz = -sz / len;
      const shots = this.multiT > 0 ? 3 : 1;
      for (let i = 0; i < shots; i++) {
        const spread = shots === 1 ? 0 : (i - 1) * 0.22;
        const c = Math.cos(spread);
        const s = Math.sin(spread);
        const vx = (dirx * c - dirz * s) * (22 + energy * 6);
        const vz = (dirx * s + dirz * c) * (22 + energy * 6);
        const mesh = new THREE.Mesh(
          new THREE.SphereGeometry(0.14, 8, 8),
          new THREE.MeshBasicMaterial({ color: this.rapidT > 0 ? 0xffee88 : 0xc8ffe8 }),
        );
        mesh.position.set(sx, 0.4, sz);
        this.kit.root.add(mesh);
        this.bullets.push({ mesh, vx, vy: vz }); // vy reused as vz
      }
      this.fireCd = fireRate;
      if (navigator.vibrate) navigator.vibrate(4);
    }

    const spawnEvery = Math.max(0.25, 0.75 - energy * 0.25 - this.wave * 0.015);
    if (this.spawnCd <= 0 || (beat > 0.2 && this.spawnCd < spawnEvery * 0.5)) {
      this.spawnEnemy(energy);
      if (energy > 0.6) this.spawnEnemy(energy);
      this.spawnCd = spawnEvery;
      this.wave = Math.min(15, 1 + Math.floor(this.score / 450));
    }
    if (this.powerCd <= 0) {
      this.spawnPower();
      this.powerCd = 7 + Math.random() * 4;
    }

    for (const b of this.bullets) {
      b.mesh.position.x += b.vx * dt;
      b.mesh.position.z += b.vy * dt;
    }
    this.bullets = this.bullets.filter((b) => {
      const px = b.mesh.position.x;
      const pz = b.mesh.position.z;
      if (Math.hypot(px, pz) < 0.9 || Math.hypot(px, pz) > 14) {
        this.kit.root.remove(b.mesh);
        disposeObject(b.mesh);
        return false;
      }
      for (const e of this.enemies) {
        if (
          Math.abs(px - e.mesh.position.x) < 0.75 &&
          Math.abs(pz - e.mesh.position.z) < 0.75
        ) {
          e.hp -= 1;
          this.kit.root.remove(b.mesh);
          disposeObject(b.mesh);
          if (e.hp <= 0) {
            this.score += e.kind === 'tank' ? 50 : e.kind === 'gem' ? 35 : 20;
            this.burst(e.mesh.position.x, 0.5, e.mesh.position.z, 0xff88aa);
            e.mesh.visible = false;
          }
          return false;
        }
      }
      return true;
    });

    for (const e of this.enemies) {
      e.r += e.vr * dt;
      e.angle += dt * 0.25 * (e.kind === 'gem' ? 1.5 : 1);
      e.mesh.position.set(Math.cos(e.angle) * e.r, 0.4, Math.sin(e.angle) * e.r);
      e.mesh.rotation.y += dt * 2;
      // hit player if near orbit
      if (
        this.invuln <= 0 &&
        e.kind !== 'gem' &&
        Math.abs(e.r - this.radius) < 0.7 &&
        Math.abs(Math.atan2(Math.sin(e.angle - this.angle), Math.cos(e.angle - this.angle))) < 0.28
      ) {
        this.hit();
        e.mesh.visible = false;
      }
      if (e.r > this.rMax + 1.5) e.mesh.visible = false;
    }
    this.enemies = this.enemies.filter((e) => {
      if (!e.mesh.visible) {
        this.kit.root.remove(e.mesh);
        disposeObject(e.mesh);
        return false;
      }
      return true;
    });

    for (const p of this.powers) {
      p.mesh.rotation.y += dt * 2;
      p.mesh.position.y = 0.5 + Math.sin(t * 3 + p.angle) * 0.15;
      if (
        Math.abs(p.r - this.radius) < 0.85 &&
        Math.abs(Math.atan2(Math.sin(p.angle - this.angle), Math.cos(p.angle - this.angle))) < 0.3
      ) {
        if (p.kind === 'multi') {
          this.multiT = 8;
          this.message = 'MULTI SHOT';
        } else if (p.kind === 'rapid') {
          this.rapidT = 8;
          this.message = 'RAPID FIRE';
        } else {
          this.shieldT = 10;
          this.message = 'SHIELD';
        }
        this.messageT = 1.1;
        this.score += 25;
        p.mesh.visible = false;
      }
    }
    this.powers = this.powers.filter((p) => {
      if (!p.mesh.visible) {
        this.kit.root.remove(p.mesh);
        disposeObject(p.mesh);
        return false;
      }
      return true;
    });

    for (const p of this.particles) {
      p.life -= dt;
      p.mesh.position.x += p.vx * dt;
      p.mesh.position.z += p.vy * dt;
      (p.mesh.material as THREE.MeshBasicMaterial).opacity = Math.max(0, p.life * 2);
    }
    this.particles = this.particles.filter((p) => {
      if (p.life <= 0) {
        this.kit.root.remove(p.mesh);
        disposeObject(p.mesh);
        return false;
      }
      return true;
    });

    // shield visual
    if (this.shieldT > 0) {
      this.ship.scale.setScalar(1.08 + Math.sin(t * 8) * 0.04);
    } else {
      this.ship.scale.setScalar(1 + kick * 0.05);
    }

    this.score += Math.floor(dt * (8 + energy * 12));
    this.orbit.apply(this.kit.camera, this.camTarget, dt);
    this.kit.renderer.render(this.kit.scene, this.kit.camera);
  }

  resize(w: number, h: number): void {
    if (!this.kit) return;
    resizeThree(this.kit, w, h, 24, 28);
  }

  getHud() {
    const buffs = [
      this.shieldT > 0 ? '🛡' : '',
      this.multiT > 0 ? '✖' : '',
      this.rapidT > 0 ? '⚡' : '',
    ]
      .join('')
      .trim();
    return {
      score: this.score,
      lives: Math.max(0, this.lives),
      wave: this.wave,
      message: this.messageT > 0 ? this.message : buffs || undefined,
    };
  }

  dispose(): void {
    this.disposed = true;
    this.orbit.detach();
    for (const e of this.enemies) disposeObject(e.mesh);
    for (const b of this.bullets) disposeObject(b.mesh);
    for (const p of this.powers) disposeObject(p.mesh);
    for (const p of this.particles) disposeObject(p.mesh);
    this.kit?.dispose();
  }
}
